import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogClose } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { X, ZoomIn, ExternalLink } from "lucide-react";

interface ImageLightboxProps {
  src: string;
  alt: string;
  title?: string;
  className?: string;
  children?: React.ReactNode;
  linkTo?: string;
  onImageClick?: () => void;
  showZoomIcon?: boolean;
  showLinkIcon?: boolean;
}

export const ImageLightbox = ({
  src,
  alt,
  title,
  className = "",
  children,
  linkTo,
  onImageClick,
  showZoomIcon = true,
  showLinkIcon = false
}: ImageLightboxProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();

  const handleImageClick = () => {
    if (onImageClick) {
      onImageClick();
    } else if (linkTo) {
      navigate(linkTo);
    } else {
      setIsOpen(true);
    }
  };

  return (
    <>
      <div 
        className={`relative group cursor-pointer overflow-hidden rounded-lg ${className} touch-manipulation select-none`}
        onClick={handleImageClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleImageClick();
          }
        }}
        aria-label={linkTo ? `Navigate to ${linkTo}` : `View ${alt} in full size`}
      >
        {children ? (
          children
        ) : (
          <img 
            src={src} 
            alt={alt}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 select-none"
            loading="lazy"
            draggable={false}
            onError={(e) => {
              const target = e.currentTarget as HTMLImageElement;
              target.style.display = 'none';
              target.parentElement?.classList.add('bg-muted', 'flex', 'items-center', 'justify-center');
              const fallback = document.createElement('div');
              fallback.className = 'text-muted-foreground text-sm p-4 text-center';
              fallback.textContent = 'Image unavailable';
              target.parentElement?.appendChild(fallback);
            }}
          />
        )}
        
        {/* Overlay with icons */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center pointer-events-none">
          <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex gap-2">
            {showZoomIcon && !linkTo && (
              <div className="bg-white/90 backdrop-blur-sm rounded-full p-2 sm:p-3 shadow-lg touch-manipulation">
                <ZoomIn className="w-4 h-4 sm:w-5 sm:h-5 text-foreground" />
              </div>
            )}
            {showLinkIcon && linkTo && (
              <div className="bg-white/90 backdrop-blur-sm rounded-full p-2 sm:p-3 shadow-lg touch-manipulation">
                <ExternalLink className="w-4 h-4 sm:w-5 sm:h-5 text-foreground" />
              </div>
            )}
          </div>
        </div>

        {title && (
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3 sm:p-4">
            <p className="text-white text-xs sm:text-sm font-medium leading-tight">{title}</p>
          </div>
        )}
      </div>

      {/* Lightbox Modal */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-[95vw] max-h-[95vh] w-full h-full p-1 sm:p-2 md:p-4 bg-black/95 border-0 overflow-hidden">
          <div className="relative w-full h-full flex items-center justify-center">
            <DialogClose asChild>
              <Button
                variant="ghost"
                size="icon"
                className="absolute top-2 right-2 sm:top-4 sm:right-4 z-50 bg-white/10 hover:bg-white/20 text-white rounded-full touch-manipulation min-h-[44px] min-w-[44px]"
                aria-label="Close lightbox"
              >
                <X className="h-4 w-4 sm:h-5 sm:w-5 md:h-6 md:w-6" />
              </Button>
            </DialogClose>
            
            <img loading="lazy" decoding="async"
              src={src}
              alt={alt}
              className="max-w-full max-h-full object-contain rounded-lg select-none"
              style={{ 
                maxWidth: 'calc(95vw - 0.5rem)', 
                maxHeight: 'calc(95vh - 0.5rem)',
                touchAction: 'pan-x pan-y'
              }}
              draggable={false}
              onError={(e) => {
                const target = e.currentTarget as HTMLImageElement;
                target.style.display = 'none';
                const container = target.parentElement;
                if (container) {
                  container.innerHTML = '<div class="text-white text-center p-8"><p class="text-lg mb-2">Image could not be loaded</p><p class="text-sm opacity-70">Please try again later</p></div>';
                }
              }}
            />
            
            {title && (
              <div className="absolute bottom-2 left-2 right-2 sm:bottom-4 sm:left-4 sm:right-4 text-center">
                <h3 className="text-white text-sm sm:text-lg md:text-xl font-semibold bg-black/50 backdrop-blur-sm rounded-lg px-2 py-1 sm:px-3 sm:py-2 md:px-4 md:py-2 inline-block max-w-full break-words">
                  {title}
                </h3>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ImageLightbox;